import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import { flushSync } from "react-dom";
import type { PortalSession, PortalUserProfile } from "@/types/portal";
import {
  announceSessionExpired,
  isOwnedSession,
  isSameUser,
  REFRESH_REFUSED_STATUSES,
  resolveBaseUrl,
  setSessionOwner,
  TOKEN_REFRESHED_EVENT,
  type TokenRefreshedEvent,
} from "@/lib/portalApi";
import { syncDecalPreferenceFromAccount } from "@/components/portal/charts/decalPreference";
import { forgetSelectedOrganisationId } from "@/lib/selectedOrganisationStorage";

const STORAGE_KEY = "lara-portal-session";

type AuthContextValue = {
  session: PortalSession | null;
  user: PortalUserProfile | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<LoginResult>;
  verifyMfa: (
    code: string,
    reference: string,
    trustDevice?: boolean,
  ) => Promise<LoginResult>;
  refreshSession: () => Promise<boolean>;
  logout: () => Promise<void>;
  setSession: (session: PortalSession) => void;
};

type LoginResult =
  | { success: true }
  | { success: false; error: string }
  | { success: false; emailNotVerified: true; email: string }
  | {
      success: false;
      verificationRequired: true;
      verificationReference?: string;
    };

const AuthContext = createContext<AuthContextValue | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

function getStoredSession(): PortalSession | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored) as PortalSession;
    if (!parsed?.token) return null;
    return parsed;
  } catch {
    return null;
  }
}

function storeSession(session: PortalSession): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
}

function clearStoredSession(): void {
  localStorage.removeItem(STORAGE_KEY);
}

type AuthProviderProps = {
  children: ReactNode;
};

export function AuthProvider({ children }: AuthProviderProps) {
  const [session, setSessionState] = useState<PortalSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize from localStorage on mount, refreshing stale user data if needed
  useEffect(() => {
    const initSession = async () => {
      const stored = getStoredSession();
      if (!stored) {
        setIsLoading(false);
        return;
      }

      // Check if session has stale user data (missing is_admin field)
      if (stored.user && stored.user.is_admin === undefined) {
        try {
          // Fetch fresh user data
          const response = await fetch(`${resolveBaseUrl()}/api/v1/users/me`, {
            headers: { Authorization: `Bearer ${stored.token}` },
          });

          if (response.ok) {
            const userData = await response.json();
            const roleLevel = userData.is_admin
              ? 1
              : userData.role_level || 4;
            const role = userData.is_admin ? "SuperAdmin" : "User";

            const refreshedSession: PortalSession = {
              ...stored,
              user: {
                firstName: userData.first_name,
                lastName: userData.last_name,
                email: userData.email,
                role,
                roleLevel,
                is_admin: userData.is_admin === true,
              },
            };
            setSessionState(refreshedSession);
            storeSession(refreshedSession);
            setIsLoading(false);
            return;
          }
        } catch {
          // If fetch fails, use stored session as-is
        }
      }

      setSessionState(stored);
      setIsLoading(false);
    };

    void initSession();
  }, []);

  // Listen for token refresh events from portalApi
  // This keeps React state in sync when portalFetch refreshes an expired token
  useEffect(() => {
    const handleTokenRefresh = (event: TokenRefreshedEvent) => {
      const { token: newToken, refreshToken: newRefreshToken } = event.detail;
      if (!newToken) return;
      // Update THIS tab's session, never rebuild it from storage: storage is
      // shared and may by now hold another user's session.
      setSessionState((current) =>
        current
          ? {
              ...current,
              token: newToken,
              refreshToken: newRefreshToken || current.refreshToken,
              // When this access token was issued; useSessionTimeout renews from it.
              createdAt: Date.now(),
            }
          : current,
      );
    };

    window.addEventListener(
      TOKEN_REFRESHED_EVENT,
      handleTokenRefresh as EventListener,
    );

    return () => {
      window.removeEventListener(
        TOKEN_REFRESHED_EVENT,
        handleTokenRefresh as EventListener,
      );
    };
  }, []);

  // Tell portalApi whose session this tab is in, so its 401 retry and token
  // refresh never pick up another user's session from shared storage.
  useEffect(() => {
    setSessionOwner(session?.user?.email ?? null);
  }, [session?.user?.email]);

  // Adopt a session another tab stored for the same user: a renewal there,
  // or a sign-in from the "signed out" dialog's new tab, which brings this
  // tab back without a reload. A removal is ignored on purpose: this tab
  // keeps its page (and any unsaved edits) and finds out on its next request.
  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== STORAGE_KEY || !event.newValue) return;
      let incoming: PortalSession;
      try {
        incoming = JSON.parse(event.newValue) as PortalSession;
      } catch {
        return;
      }
      if (!incoming?.token) return;
      setSessionState((current) => {
        if (!current || current.token === incoming.token) return current;
        if (!isSameUser(current.user?.email, incoming.user?.email)) return current;
        return incoming;
      });
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  // Reconcile the chart decal preference against the account-scoped CP
  // value once a session is available (decalPreference.ts, D2/O2). The
  // synchronous localStorage restore in initDecalPreference() (main.tsx)
  // already painted a best-guess state before auth resolved; this brings it
  // in line with the roaming value (or the account-conditional default).
  useEffect(() => {
    if (!session?.token) return;
    void syncDecalPreferenceFromAccount(session.token, session.user?.email);
  }, [session?.token, session?.user?.email]);

  const setSession = useCallback((newSession: PortalSession) => {
    // Someone else signing in here (after a refused refresh kept the page):
    // they start from their own orgs, not this tab's previous user's.
    if (!isOwnedSession(newSession)) {
      forgetSelectedOrganisationId();
    }
    // Use flushSync to ensure React state is updated synchronously
    // This prevents race conditions where navigation happens before state updates
    flushSync(() => {
      setSessionState(newSession);
    });
    storeSession(newSession);
  }, []);

  const login = useCallback(
    async (email: string, password: string): Promise<LoginResult> => {
      try {
        // OAuth2 password grant uses form data
        const formData = new URLSearchParams();
        formData.append("username", email);
        formData.append("password", password);

        const response = await fetch(`${resolveBaseUrl()}/api/v1/auth/token`, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: formData.toString(),
        });

        const data = await response.json();

        if (!response.ok) {
          // Handle various error formats
          const errorMsg =
            data.detail || data.error || data.message || "Sign in failed";
          // An unverified email is a 403 with a specific detail. Surface it
          // distinctly so the sign-in form can offer a "resend verification"
          // path instead of a dead-end error (the user is otherwise stuck:
          // they can't register again and can't sign in).
          if (
            response.status === 403 &&
            typeof errorMsg === "string" &&
            errorMsg.toLowerCase().includes("not verified")
          ) {
            return { success: false, emailNotVerified: true, email };
          }
          return { success: false, error: errorMsg };
        }

        // MFA required (check for mfa_required flag from control-plane)
        if (data.mfa_required || data.verificationRequired) {
          return {
            success: false,
            verificationRequired: true,
            verificationReference: data.mfa_token || data.verificationReference,
          };
        }

        // OAuth2 token response format from control-plane
        if (data.access_token && data.refresh_token && data.user) {
          // Map is_admin to roleLevel (1 = SuperAdmin, 4 = User)
          const roleLevel = data.user.is_admin
            ? 1
            : data.user.role_level || data.user.roleLevel || 4;
          const role = data.user.is_admin
            ? "SuperAdmin"
            : data.user.role || "User";

          const newSession: PortalSession = {
            token: data.access_token,
            refreshToken: data.refresh_token,
            user: {
              firstName: data.user.first_name || data.user.firstName,
              lastName: data.user.last_name || data.user.lastName,
              email: data.user.email,
              role,
              roleLevel,
              is_admin: data.user.is_admin === true,
            },
            createdAt: Date.now(),
          };
          setSession(newSession);
          return { success: true };
        }

        return { success: false, error: "Invalid response from server" };
      } catch {
        return { success: false, error: "Network error. Please try again." };
      }
    },
    [setSession],
  );

  const verifyMfa = useCallback(
    async (
      code: string,
      reference: string,
      trustDevice = false,
    ): Promise<LoginResult> => {
      try {
        const response = await fetch(`${resolveBaseUrl()}/api/v1/auth/mfa/verify/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            mfa_token: reference,
            code,
            trust_device: trustDevice,
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          return {
            success: false,
            error: data.detail || data.error || "Verification failed",
          };
        }

        // OAuth2 token response format from control-plane
        if (data.access_token && data.refresh_token && data.user) {
          // Map is_admin to roleLevel (1 = SuperAdmin, 4 = User)
          const roleLevel = data.user.is_admin
            ? 1
            : data.user.role_level || data.user.roleLevel || 4;
          const role = data.user.is_admin
            ? "SuperAdmin"
            : data.user.role || "User";

          const newSession: PortalSession = {
            token: data.access_token,
            refreshToken: data.refresh_token,
            user: {
              firstName: data.user.first_name || data.user.firstName,
              lastName: data.user.last_name || data.user.lastName,
              email: data.user.email,
              role,
              roleLevel,
              is_admin: data.user.is_admin === true,
            },
            createdAt: Date.now(),
          };
          setSession(newSession);
          return { success: true };
        }

        return { success: false, error: "Invalid response from server" };
      } catch {
        return { success: false, error: "Network error. Please try again." };
      }
    },
    [setSession],
  );

  const refreshSession = useCallback(async (): Promise<boolean> => {
    const currentSession = getStoredSession();
    if (!currentSession?.refreshToken) {
      return false;
    }
    if (!isOwnedSession(currentSession)) {
      // Another user signed in elsewhere; this tab's own session has ended.
      announceSessionExpired();
      return false;
    }

    try {
      const response = await fetch(`${resolveBaseUrl()}/api/v1/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: currentSession.refreshToken }),
      });

      if (!response.ok) {
        if (REFRESH_REFUSED_STATUSES.has(response.status)) {
          // The refresh token was refused: the session has ended. Keep the
          // in-memory session so the page (and any unsaved edits) stays up;
          // the shell's "signed out" dialog offers a sign-in in a new tab.
          clearStoredSession();
          announceSessionExpired();
        }
        return false;
      }

      const data = await response.json();

      if (data.access_token) {
        // Use fresh user data from refresh response if available, otherwise keep current
        let user = currentSession.user;
        if (data.user) {
          const roleLevel = data.user.is_admin
            ? 1
            : data.user.role_level || data.user.roleLevel || 4;
          const role = data.user.is_admin
            ? "SuperAdmin"
            : data.user.role || "User";
          user = {
            firstName: data.user.first_name || data.user.firstName,
            lastName: data.user.last_name || data.user.lastName,
            email: data.user.email,
            role,
            roleLevel,
            is_admin: data.user.is_admin === true,
          };
        }

        const newSession: PortalSession = {
          token: data.access_token,
          refreshToken: data.refresh_token || currentSession.refreshToken,
          user,
          // When this access token was issued; useSessionTimeout renews from it.
          createdAt: Date.now(),
        };
        setSessionState(newSession);
        storeSession(newSession);
        return true;
      }

      return false;
    } catch {
      return false;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      if (session?.token) {
        await fetch(`${resolveBaseUrl()}/api/v1/users/logout`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${session.token}`,
          },
        });
      }
    } catch {
      // Ignore network errors during logout
    } finally {
      setSessionState(null);
      clearStoredSession();
      // The next person to sign in starts from their own orgs.
      forgetSelectedOrganisationId();
      // Clear any legacy session cookies from Next.js
      document.cookie =
        "lara-portal-session=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
    }
  }, [session?.token]);

  const value: AuthContextValue = {
    session,
    user: session?.user ?? null,
    token: session?.token ?? null,
    isAuthenticated: !!session?.token,
    isLoading,
    login,
    verifyMfa,
    refreshSession,
    logout,
    setSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
